/**
 * Recommendations Service - Frontend client for recommendations API
 */

import { supabase } from '../lib/supabase';
import API_URL from '../config/api';

const logger = {
  info: (msg, data) => console.log(`[Recommendations] ${msg}`, data || ''),
  error: (msg, data) => console.error(`[Recommendations] ${msg}`, data || ''),
  warn: (msg, data) => console.warn(`[Recommendations] ${msg}`, data || ''),
};

// Feature flag: Stage 7 Recommendations API
// Set VITE_FEATURE_STAGE7=true in .env to enable real API calls
// When false (default), returns empty results without making API calls
const FEATURE_STAGE7_ENABLED = import.meta.env.VITE_FEATURE_STAGE7 === 'true';

// Log feature flag status once at startup
if (FEATURE_STAGE7_ENABLED) {
  logger.info('Stage 7 enabled — live mode active');
} else {
  logger.info('Stage 7 disabled — using local fallback');
}

export class RecommendationsService {
  constructor() {
    this.baseUrl = API_URL.replace('/v1', '') || 'http://localhost:4028';
    this.featureEnabled = FEATURE_STAGE7_ENABLED;
  }

  /**
   * Check if API is available and handle errors gracefully
   * @private
   */
  async safeFetch(url, options) {
    if (!this.featureEnabled) {
      // Stage 7 disabled - return fallback immediately
      return null;
    }

    try {
      const response = await fetch(url, options);
      
      // Handle 5xx server errors
      if (response.status >= 500) {
        logger.warn('Server error, using fallback', { status: response.status });
        return null;
      }
      
      return response;
    } catch (error) {
      // Handle network errors (ERR_CONNECTION_REFUSED, etc.)
      if (error.name === 'TypeError' || error.message?.includes('fetch')) {
        logger.warn('Network error, using fallback', { error: error.message });
        return null;
      }
      
      // Re-throw other errors
      throw error;
    }
  }

  /**
   * Get recommendations for current user (legacy - all sections)
   * @returns {Promise<{forYou: any[], becauseYouLiked: any[], continueExploring: any[]}>}
   */
  async getRecommendations() {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await this.safeFetch(`${this.baseUrl}/v1/recommendations`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      // Fallback: return empty results if API unavailable
      if (!response) {
        return {
          forYou: [],
          becauseYouLiked: [],
          continueExploring: [],
        };
      }

      if (!response.ok) {
        throw new Error(`Failed to get recommendations: ${response.statusText}`);
      }

      const result = await response.json();
      return result.data || {
        forYou: [],
        becauseYouLiked: [],
        continueExploring: [],
      };
    } catch (error) {
      // Only log errors if feature is enabled (avoid spam when disabled)
      if (this.featureEnabled) {
        logger.error('Failed to get recommendations', { error });
      }
      
      // Return fallback instead of throwing
      return {
        forYou: [],
        becauseYouLiked: [],
        continueExploring: [],
      };
    }
  }

  /**
   * Get section recommendations with pagination and transparency
   * @param {string} section - 'forYou' | 'liked' | 'continue' | 'trending'
   * @param {number} page - Page number (default: 1)
   * @param {number} pageSize - Items per page (default: 20)
   * @returns {Promise<RecommendationsResponseDto>}
   */
  async getSectionRecommendations(section, page = 1, pageSize = 20) {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const params = new URLSearchParams({
        section,
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      const response = await this.safeFetch(`${this.baseUrl}/v1/recommendations?${params}`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      // Fallback: return empty results if API unavailable
      if (!response) {
        return {
          results: [],
          section,
          page,
          pageSize,
          hasMore: false,
          metadata: null,
          fallback: true,
        };
      }

      if (!response.ok) {
        throw new Error(`Failed to get section recommendations: ${response.statusText}`);
      }

      const result = await response.json();
      return result.data || {
        results: [],
        section,
        page,
        pageSize,
        hasMore: false,
        metadata: null,
      };
    } catch (error) {
      // Only log errors if feature is enabled (avoid spam when disabled)
      if (this.featureEnabled) {
        logger.error('Failed to get section recommendations', { error, section, page });
      }
      
      // Return fallback instead of throwing
      return {
        results: [],
        section,
        page,
        pageSize,
        hasMore: false,
        metadata: null,
        fallback: true,
      };
    }
  }
}

// Export singleton
export const recommendationsService = new RecommendationsService();

